import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ProductCard } from '../components/common/ProductCard';
import { getProducts } from '../utils/api';
import { Filter } from 'lucide-react';
import { useLanguage } from '../store/languageContext';
import translate from '../utils/translation';

const ProductsPage = () => {
    const [products, setProducts] = useState([]);
    const [rawProducts, setRawProducts] = useState([]);
    const [parsedRawProducts, setParsedRawProducts] = useState([]);
    const [filters, setFilters] = useState({ brand: '', category: '', minPrice: 0, maxPrice: 10000, search: '', sort: 'update_date' });
    const [selectedSpecs, setSelectedSpecs] = useState({});
    const [metadata, setMetadata] = useState({ minPrice: 0, maxPrice: 10000, uniqueSpecs: {} });
    const [availableSpecs, setAvailableSpecs] = useState({});
    const [specCounts, setSpecCounts] = useState({});

    const sectionRef = useRef(null);
    const { language, setNavColor } = useLanguage();
    const t = (key) => translate[language]?.[key] || key;

    // Fetch metadata (all products for initial min/max prices and unique specs)
    useEffect(() => {
        getProducts({ limit: 1000 }).then(res => {
            const data = res.data || [];
            if (data.length === 0) return;

            const prices = data.map(p => parseFloat(p.price || 0));
            const minP = prices.length > 0 ? Math.min(...prices) : 0;
            const maxP = prices.length > 0 ? Math.max(...prices) : 10000;

            const specsMap = data.reduce((acc, p) => {
                let specs = {};
                try {
                    specs = typeof p.specifications === 'string' ? JSON.parse(p.specifications) : (p.specifications || {});
                } catch (e) {
                    specs = {};
                }
                Object.entries(specs).forEach(([k, v]) => {
                    if (!acc[k]) acc[k] = new Set();
                    acc[k].add(v);
                });
                return acc;
            }, {});

            setMetadata({ minPrice: minP, maxPrice: maxP, uniqueSpecs: specsMap });

            // Update default maxPrice in filters
            setFilters(prev => ({ ...prev, maxPrice: maxP }));
        }).catch(err => console.error('Error fetching metadata:', err));
    }, []);

    // Fetch raw products from server filters
    useEffect(() => {
        getProducts(filters).then(res => {
            const raw = res.data || [];
            setRawProducts(raw);
        }).catch(err => {
            console.error('Error fetching products:', err);
            setRawProducts([]);
        });
    }, [filters]);

    // Parse raw products once
    useEffect(() => {
        const parsed = rawProducts.map(p => ({
            ...p,
            specs: (() => {
                try {
                    return typeof p.specifications === 'string' ? JSON.parse(p.specifications) : (p.specifications || {});
                } catch (e) {
                    return {};
                }
            })()
        }));
        setParsedRawProducts(parsed);
    }, [rawProducts]);

    // Compute available specs from parsed raw products
    useEffect(() => {
        if (parsedRawProducts.length === 0) {
            setAvailableSpecs({});
            return;
        }
        const specsMap = parsedRawProducts.reduce((acc, p) => {
            Object.entries(p.specs).forEach(([k, v]) => {
                if (!acc[k]) acc[k] = new Set();
                acc[k].add(v);
            });
            return acc;
        }, {});
        setAvailableSpecs(specsMap);
    }, [parsedRawProducts]);

    // Compute spec counts based on current selections (ignoring own key)
    useEffect(() => {
        if (parsedRawProducts.length === 0 || Object.keys(availableSpecs).length === 0) {
            setSpecCounts({});
            return;
        }
        const counts = {};
        Object.entries(availableSpecs).forEach(([key, valSet]) => {
            counts[key] = {};
            Array.from(valSet).forEach(value => {
                const count = parsedRawProducts.filter(p => {
                    let matchesOthers = Object.entries(selectedSpecs).every(([oKey, oVals]) =>
                        oKey === key || oVals.has(p.specs[oKey])
                    );
                    return matchesOthers && p.specs[key] === value;
                }).length;
                counts[key][value] = count;
            });
        });
        setSpecCounts(counts);
    }, [parsedRawProducts, selectedSpecs, availableSpecs]);

    // Filter final products based on selected specs
    useEffect(() => {
        const filteredProducts = parsedRawProducts.filter(p =>
            Object.entries(selectedSpecs).every(([key, vals]) => vals.has(p.specs[key]))
        );
        setProducts(filteredProducts);
    }, [parsedRawProducts, selectedSpecs]);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setNavColor('white');
                }
            },
            {
                threshold: 0.1,
                rootMargin: '-50px 0px -20% 0px',
            }
        );

        const currentRef = sectionRef.current;
        if (currentRef) {
            observer.observe(currentRef);
        }

        return () => {
            if (currentRef) {
                observer.unobserve(currentRef);
            }
        };
    }, [setNavColor]);

    const handleFilterChange = (key, value) => {
        setFilters(prev => ({ ...prev, [key]: value }));
    };

    const handleSpecChange = (key, value, isChecked) => {
        setSelectedSpecs(prev => {
            const newState = { ...prev };
            if (!newState[key]) {
                newState[key] = new Set();
            }
            if (isChecked) {
                newState[key].add(value);
            } else {
                newState[key].delete(value);
                if (newState[key].size === 0) {
                    delete newState[key];
                }
            }
            return newState;
        });
    };

    return (
        <div ref={sectionRef} className="p-8 pt-[100px] bg-[#222] min-h-screen">
            <div className="flex mb-8 justify-between items-center">
                <div className="flex items-center">
                    <input
                        type="text"
                        placeholder={t('searchPlaceholder')}
                        value={filters.search}
                        onChange={e => handleFilterChange('search', e.target.value)}
                        className="border p-2 bg-transparent text-white outline-none border-[#555] w-64"
                    />
                    <select
                        onChange={e => handleFilterChange('sort', e.target.value)}
                        value={filters.sort}
                        className="border p-2 bg-transparent text-white outline-none border-[#555] mx-4 w-64"
                    >
                        <option value="update_date">{t('sortLatest')}</option>
                        <option value="price_asc">{t('sortPriceLowToHigh')}</option>
                        <option value="price_desc">{t('sortPriceHighToLow')}</option>
                    </select>
                </div>
                <Filter className="text-white" />
            </div>
            <div className="flex gap-4">
                <aside className="w-64 flex-shrink-0 bg-[#323232] text-white p-4 rounded">
                    <h3 className="font-bold mb-4">{t('filters')}</h3>
                    <div className="mb-4">
                        <label className="block mb-1">{t('minPrice')}</label>
                        <input
                            type="number"
                            min={metadata.minPrice}
                            max={metadata.maxPrice}
                            value={filters.minPrice}
                            onChange={e => handleFilterChange('minPrice', parseFloat(e.target.value) || 0)}
                            className="border p-2 w-full outline-none bg-transparent border-[#555] text-white"
                        />
                    </div>
                    <div className="mb-4">
                        <label className="block mb-1">{t('maxPrice')}</label>
                        <input
                            type="number"
                            min={metadata.minPrice}
                            max={metadata.maxPrice}
                            value={filters.maxPrice}
                            onChange={e => handleFilterChange('maxPrice', parseFloat(e.target.value) || metadata.maxPrice)}
                            className="border p-2 w-full outline-none bg-transparent border-[#555] text-white"
                        />
                    </div>
                    {Object.entries(availableSpecs).map(([key, valSet]) => (
                        <div key={key} className="mb-4">
                            <h4 className="font-bold mb-2">{key}</h4>
                            {Array.from(valSet).map(val => {
                                const isSelected = (selectedSpecs[key] || new Set()).has(val);
                                const count = specCounts[key]?.[val] || 0;
                                const disabled = count === 0 && !isSelected;
                                const id = `spec-${key}-${val}`;
                                return (
                                    <div key={val} className="flex items-center mb-1">
                                        <input
                                            type="checkbox"
                                            id={id}
                                            className="hidden"
                                            checked={isSelected}
                                            onChange={e => handleSpecChange(key, val, e.target.checked)}
                                            disabled={disabled}
                                        />
                                        <label
                                            htmlFor={id}
                                            className={`relative inline-flex items-center cursor-pointer select-none ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
                                        >
                                            <span
                                                className={`inline-flex w-6 h-6 border-2 rounded items-center justify-center ${language === 'ar' ? 'ml-2' : 'mr-2'} transition-colors ${isSelected
                                                    ? 'border-blue-500 text-blue-500'
                                                    : 'border-gray-500 text-gray-500'
                                                    } ${disabled ? 'opacity-50' : ''}`}
                                            >
                                                {isSelected && (
                                                    <svg
                                                        className="w-4 h-4"
                                                        fill="none"
                                                        stroke="currentColor"
                                                        viewBox="0 0 24 24"
                                                    >
                                                        <path
                                                            strokeLinecap="round"
                                                            strokeLinejoin="round"
                                                            strokeWidth={2}
                                                            d="M5 13l4 4L19 7"
                                                        />
                                                    </svg>
                                                )}
                                            </span>
                                            <span className={disabled ? 'opacity-50' : ''}>
                                                {String(val)} <span className='text-red-400'>({count})</span>
                                            </span>
                                        </label>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </aside>
                <div className="flex-1">
                    <AnimatePresence>
                        <div className="grid md:grid-cols-5 gap-4">
                            {products?.map(product => (
                                <motion.div
                                    key={product.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: -20 }}
                                    transition={{ duration: 0.2 }}
                                >
                                    <ProductCard product={product} />
                                </motion.div>
                            ))}
                        </div>
                    </AnimatePresence>
                </div>
            </div>
        </div>
    );
};

export default ProductsPage;